import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useParticipantStatuses } from '../hooks/useParticipantStatuses';
import { dayjs, images } from '../utils';
import { ApiContext } from './ApiProvider';
import { getAllParticipants, getParticipantStepCount, isLeadBooker } from '../utils/booking';

const SidebarContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '400px', // Prevent excessive shrinking
    overflow: 'hidden', // Ensure no scrolling at container level
}));

const TourInfoCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: 'none',
    width: '100%',
    flexShrink: 0, // Prevent card from shrinking
}));



const StyledNavItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    textDecoration: 'none',
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    cursor: 'default',
}));

const StyledNavLink = styled(NavLink)(({ theme, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    textDecoration: 'none',
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
    backgroundColor: 'transparent',
    cursor: disabled ? 'default' : 'pointer',
    pointerEvents: disabled ? 'none' : 'auto',
    fontWeight: 'bold',
    '.MuiListItemText-primary': {
        fontWeight: 600,
    },
    '&:hover': {
        backgroundColor: !disabled ? theme.palette.action.hover : 'transparent',
    },
    '&.active': {
        backgroundColor: '#EAF4FF',
    },
}));

const MobileSidebarWrapper = styled(Paper)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
    overflow: 'visible',
    marginBottom: theme.spacing(2),
}));

const LEAD_BOOKER_CHECKMARK_COLOR = '#00cda6';

const Sidebar = ({ currentStep, isSuccessStep }) => {
    const { booking, preCheckSettings, settings, currentFormData, currentParticipantIndex } = useContext(ApiContext);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [mobileOpenSteps, setMobileOpenSteps] = useState(false);

    // Refs for scroll containers
    const desktopStepsRef = useRef(null);
    const mobileStepsRef = useRef(null);

    // Use new status management with current form data from context
    const { getParticipantStatus, hasUnsavedSteps, areAllCheckedIn } = useParticipantStatuses(currentFormData, currentParticipantIndex);

    // Auto-scroll to current step when currentStep changes
    useEffect(() => {
        const scrollToCurrentStep = () => {
            const container = isDesktop ? desktopStepsRef.current : mobileStepsRef.current;
            if (!container) return;

            const currentStepElement = container.querySelector(`[data-step-index="${currentStep}"]`);
            if (currentStepElement) {
                currentStepElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        };

        const timeoutId = setTimeout(scrollToCurrentStep, 100);
        return () => clearTimeout(timeoutId);
    }, [currentStep, isDesktop, mobileOpenSteps]);

    const participantStepCount = getParticipantStepCount(booking, preCheckSettings);

    const maxReachableStep = useMemo(() => {
        if (!booking) return -1;

        // If allow_edit_after_checkin is true, all steps are always reachable
        if (preCheckSettings?.allow_edit_after_checkin === true && !isSuccessStep) {
            return participantStepCount;
        }

        const allParticipants = getAllParticipants(booking);
        const firstIncompleteIndex = allParticipants
            .slice(0, participantStepCount)
            .findIndex(p => p.status !== 'Pre-Checked-in');
        return firstIncompleteIndex === -1 ? participantStepCount : firstIncompleteIndex;
    }, [booking, preCheckSettings, isSuccessStep, participantStepCount]);

    // When collect_party_members_info is false, show only Lead Booker
    const showPartyMembers = preCheckSettings?.collect_party_members_info !== false;

    const totalSteps = participantStepCount + 1; // +1 for pre-checkin step
    const progress = currentStep < totalSteps ? ((currentStep + 1) / totalSteps) * 100 : 100;

    // Construct cover image URL
    const coverImageUrl = booking.group_tour.cover_image && settings?.images?.base
        ? images.url(settings.images.base, booking.group_tour.cover_image)
        : null;

    const renderStepLinks = () => (
        <Box>
            {getAllParticipants(booking)
                .filter((_, index) => showPartyMembers || isLeadBooker(index))
                .map((participant, index) => {
                    // When filtering, we need to use original index for navigation
                    const originalIndex = showPartyMembers ? index : 0;
                    const status = getParticipantStatus(originalIndex);
                    const isCurrentStep = currentStep === originalIndex;

                    // All steps are reachable if allow_edit_after_checkin is true, 
                    // otherwise use original logic
                    const isReachable = preCheckSettings?.allow_edit_after_checkin === true ? true : !isSuccessStep;

                    const label = isLeadBooker(originalIndex) ? `Lead Booker` : `Guest ${originalIndex}`;
                    const checkmarkColor = isLeadBooker(originalIndex) ? LEAD_BOOKER_CHECKMARK_COLOR : 'success.main';

                    let icon;
                    if (status === 'skipped') {
                        icon = <CancelIcon sx={{ color: 'text.disabled' }} fontSize="small" />;
                    } else if (isCurrentStep) {
                        if (status === 'saved' || status === 'completed') {
                            icon = <CheckCircleIcon sx={{ color: checkmarkColor }} fontSize="small" />;
                        } else if (status === 'missing_required') {
                            icon = <FiberManualRecordIcon sx={{ color: '#FE3F3580', '& circle': { r: 10 } }} fontSize="small" />;
                        } else {
                            icon = <FiberManualRecordIcon sx={{ color: 'primary.main', '& circle': { r: 10 } }} fontSize="small" />;
                        }
                    } else if (status === 'completed' || status === 'saved') {
                        icon = <CheckCircleIcon sx={{ color: checkmarkColor }} fontSize="small" />;
                    } else if (status === 'missing_required') {
                        icon = <FiberManualRecordIcon sx={{ color: '#FE3F3580', '& circle': { r: 10 } }} fontSize="small" />;
                    } else {
                        icon = <RadioButtonUncheckedOutlinedIcon sx={{ color: 'text.disabled' }} fontSize="small" />;
                    }

                    const ItemComponent = isReachable ? StyledNavLink : StyledNavItem;
                    const itemProps = isReachable ? {
                        to: `/participant/${originalIndex}`,
                        className: ({ isActive }) => isActive ? 'active' : ''
                    } : {};

                    return (
                        <ItemComponent
                            key={`participant-${originalIndex}`}
                            data-step-index={originalIndex}
                            {...itemProps}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
                            <ListItemText primary={label} />
                        </ItemComponent>
                    );
                })}
            {(() => {
                const preCheckinStepIndex = participantStepCount;
                const isCurrentPreCheckinStep = currentStep === preCheckinStepIndex;
                const isReachable = preCheckSettings?.allow_edit_after_checkin === true ? true : !isSuccessStep;
                const allAlreadyCheckedIn = areAllCheckedIn();
                // Pre-check-in step icon logic
                let icon;
                if (allAlreadyCheckedIn) {
                    icon = <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" />;
                } else if (isCurrentPreCheckinStep) {
                    // Current pre-checkin step - blue solid dot
                    icon = <FiberManualRecordIcon sx={{ color: 'primary.main', '& circle': { r: 10 } }} fontSize="small" />;
                } else {
                    // Default pre-checkin step - gray empty circle
                    icon = <RadioButtonUncheckedOutlinedIcon sx={{ color: 'text.disabled' }} fontSize="small" />;
                }

                const ItemComponent = isReachable ? StyledNavLink : StyledNavItem;
                const itemProps = isReachable ? {
                    to: "/checkin",
                    className: ({ isActive }) => isActive ? 'active' : ''
                } : {};

                return (
                    <ItemComponent
                        key="precheckin"
                        data-step-index={preCheckinStepIndex}
                        {...itemProps}
                    >
                        <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
                        <ListItemText primary="Pre-check-in" />
                    </ItemComponent>
                );
            })()}
        </Box>
    );

    if (!isDesktop) {
        return (
            <MobileSidebarWrapper>
                <TourInfoCard>
                    {coverImageUrl && (
                        <CardMedia
                            component="img"
                            image={coverImageUrl}
                            alt={booking.group_tour.name}
                            sx={{ width: '100%', display: 'block', height: 140 }}
                            loading="lazy"
                        />
                    )}
                    <CardContent sx={{ textAlign: 'left', p: theme.spacing(2) }}>
                        <Typography variant="caption" display="block" gutterBottom sx={{ fontSize: '14px' }}>
                            {dayjs.tz(booking.start_ts, "UTC").tz(booking.group_tour.start_timezone).format('MMM D, YYYY - h:mm A')}
                        </Typography>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '16px' }}>
                            {booking.group_tour?.name}
                        </Typography>
                        {booking.ref && (
                            <Typography variant="body1" component="div" color="text.secondary" sx={{ mt: 1 }}>
                                Booking:&nbsp;
                                <Chip label={booking.ref.toUpperCase()} size="small" sx={{ bgcolor: 'grey.200', mr: 0.5, borderRadius: theme.chip.borderRadius }} />
                            </Typography>
                        )}
                    </CardContent>
                </TourInfoCard>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: theme.spacing(2), py: theme.spacing(2) }}>
                    <Box sx={{ display: 'block', alignItems: 'left' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>pre-check-in process</Typography>
                        <Typography variant="body" color="text.secondary">
                            {preCheckSettings?.intro_text || "Provide participating guests' contact information to pre-check-in."}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setMobileOpenSteps(!mobileOpenSteps)}
                        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1, bgcolor: 'background.paper', color: theme.palette.primary.main }}
                    >
                        {mobileOpenSteps ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                </Box>

                {mobileOpenSteps && (
                    <Box ref={mobileStepsRef} sx={{ p: theme.spacing(2), pt: 1, maxHeight: '300px', overflowY: 'auto' }}>
                        {renderStepLinks()}
                    </Box>
                )}
            </MobileSidebarWrapper>
        );
    }

    return (
        <SidebarContainer>
            <TourInfoCard>
                {coverImageUrl && (
                    <CardMedia
                        component="img"
                        image={coverImageUrl}
                        alt={booking.group_tour.name}
                        sx={{ width: '100%', display: 'block', height: 140 }}
                    />
                )}
                <CardContent sx={{ textAlign: 'left', p: theme.spacing(2) }}>
                    <Typography variant="caption" display="block" gutterBottom
                        sx={{ fontSize: '14px', color: 'text.secondary' }} >
                        {dayjs.tz(booking.start_ts, "UTC").tz(booking.group_tour.start_timezone).format('MMM D, YYYY - h:mm A')}
                    </Typography>
                    <Typography variant="h6" component="div"
                        sx={{ fontWeight: 600, fontSize: '16px' }}
                    >
                        {booking.group_tour?.name}
                    </Typography>
                    {booking.ref && (
                        <Typography variant="body1" component="div" color="text.secondary" sx={{ mt: 1 }}>
                            Booking:&nbsp;
                            <Chip
                                key={booking.ref}
                                label={booking.ref.toUpperCase()}
                                size="small"
                                sx={{
                                    bgcolor: 'grey.200',
                                    color: 'text.secondary',
                                    mr: 0.8,
                                    mb: 0.8,
                                    borderRadius: theme.chip.borderRadius,
                                    fontWeight: 600,
                                    fontFamily: 'Roboto',
                                    fontSize: '12px',
                                    lineHeight: '20px',
                                }}
                            />
                        </Typography>
                    )}
                </CardContent>
            </TourInfoCard>

            <Divider />

            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
            }}>
                <Box sx={{ p: theme.spacing(2), pb: theme.spacing(1) }}>
                    <Typography variant="h6" align="left" gutterBottom sx={{ fontSize: '16px' }}>
                        Pre-check-in process
                    </Typography>
                    <Typography variant="body2" align="left" color="text.secondary" sx={{ mb: 2 }}>
                        {preCheckSettings?.intro_text || "Provide participating guests' contact information to pre-check-in."}
                    </Typography>
                </Box>

                <Box ref={desktopStepsRef} sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: theme.spacing(2),
                    pb: theme.spacing(1)
                }}>
                    {renderStepLinks()}
                </Box>
            </Box>

            {!isSuccessStep && <Divider sx={{ my: 2 }} />}

            {!isSuccessStep && (
                <Box sx={{ px: theme.spacing(2), pb: theme.spacing(2) }}>
                    <Typography variant="body1" gutterBottom align="left">
                        Step {currentStep + 1} of {totalSteps}
                    </Typography>
                    {isDesktop && (
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 2, mt: 1 }}
                        />
                    )}
                </Box>
            )}
        </SidebarContainer>
    );
};

export default Sidebar; 